import { useState, useMemo } from 'react'
import {
  Container,
  Title,
  Stack,
  Card,
  Text,
  Group,
  Button,
  Loader,
  Center,
  Badge,
  Table,
  Paper,
  Tabs,
  ScrollArea,
  Pagination,
  TextInput,
} from '@mantine/core'
import { IconSearch, IconRefresh, IconClipboardCheck } from '@tabler/icons-react'
import { useFeedbackApi } from '@/hooks/useFeedbackApi'
import { usePermissions } from '@/hooks/usePermissions'
import { useComplianceDashboard } from '@/hooks/useAnalytics'
import { OffenseDetailDialog } from '@/components/OffenseDetailDialog'
import type { FeedbackRequest, FeedbackStatus } from '@/types/feedback.types'
import type { ComplianceDashboardData } from '@/types'
import { useQuery } from '@tanstack/react-query'

// Helper to format dates that may come as BigQuery objects { value: "YYYY-MM-DD" }
const formatDate = (value: any): string => {
  if (!value) return 'N/A'
  try {
    // BigQuery dates may come as objects with a `value` property
    const dateValue = typeof value === 'object' && value?.value ? value.value : value
    if (!dateValue) return 'N/A'
    const date = new Date(dateValue)
    if (isNaN(date.getTime())) return 'N/A'
    return date.toLocaleDateString()
  } catch {
    return 'N/A'
  }
}

export function ExpertFeedbackPage() {
  const { isExpert, isAdmin, effectiveTenantId } = usePermissions()
  const { getPendingRequests, getMyRequests, getFeedbackStats } = useFeedbackApi()

  // State
  const [selectedRequest, setSelectedRequest] = useState<FeedbackRequest | null>(null)
  const [dialogOpened, setDialogOpened] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('todo')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 20

  // Fetch data based on active tab
  const { data: requests, isPending: isLoading, refetch } = useQuery({
    queryKey: ['feedback-requests', effectiveTenantId ?? 'portfolio', activeTab],
    queryFn: async () => {
      if (activeTab === 'todo') {
        return getPendingRequests()
      } else if (activeTab === 'assigned') {
        return getMyRequests({})
      } else if (activeTab === 'completed') {
        return getMyRequests({ feedback_status: 'COMPLETED' as FeedbackStatus })
      }
      return []
    },
    enabled: isExpert || isAdmin,
    placeholderData: (previous) => previous,
  })
  const requestList = requests ?? []

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['feedback-stats', effectiveTenantId ?? 'portfolio'],
    queryFn: getFeedbackStats,
    enabled: isExpert || isAdmin,
  })

  // Filter and paginate
  const filteredRequests = useMemo(() => {
    let filtered = requestList

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter((req: FeedbackRequest) =>
        req.case_number?.toLowerCase().includes(query) ||
        req.offense_id?.toLowerCase().includes(query) ||
        req.search_id?.toLowerCase().includes(query) ||
        req.charge?.toLowerCase().includes(query)
      )
    }

    return filtered
  }, [requestList, searchQuery])

  const paginatedRequests = useMemo(() => {
    const start = (page - 1) * pageSize
    return filteredRequests.slice(start, start + pageSize)
  }, [filteredRequests, page])

  const totalPages = Math.ceil(filteredRequests.length / pageSize)

  const handleRowClick = (request: FeedbackRequest) => {
    setSelectedRequest(request)
    setDialogOpened(true)
  }

  const handleDialogClose = () => {
    setDialogOpened(false)
    setSelectedRequest(null)
  }

  const handleFeedbackSubmitted = () => {
    refetch()
    handleDialogClose()
  }

  // Fetch all compliance data for the dialog (use wide date range to get all data)
  const today = new Date()
  const oneYearAgo = new Date(today)
  oneYearAgo.setFullYear(today.getFullYear() - 1)

  const selectedSearchId = selectedRequest?.search_id
  const { data: complianceDashboard } = useComplianceDashboard({
    startDate: selectedSearchId ? oneYearAgo.toISOString().split('T')[0] : '',
    endDate: selectedSearchId ? today.toISOString().split('T')[0] : '',
    page: 1,
    pageSize: 50,
    searchId: selectedSearchId || '',
    includeMetrics: false,
    includeFilterOptions: false,
  })

  // Get offenses for the selected search (paged)
  const allOffensesInSearch = useMemo(() => {
    if (!selectedSearchId) return []
    return (complianceDashboard?.rows || []).filter(
      (offense: ComplianceDashboardData) => offense.search_id === selectedSearchId
    )
  }, [selectedSearchId, complianceDashboard])

  // Convert FeedbackRequest to ComplianceDashboardData for the dialog
  const selectedOffense: ComplianceDashboardData | null = selectedRequest ? {
    offense_id: selectedRequest.offense_id,
    search_id: selectedRequest.search_id,
    case_number: selectedRequest.case_number || selectedRequest.offense_case_number || '',
    charge: selectedRequest.charge,
    charge_description: selectedRequest.charge_description,
    charge_norm: selectedRequest.charge_norm,
    disposition: selectedRequest.disposition,
    disp_class: selectedRequest.disp_class,
    disposition_norm: selectedRequest.disposition_norm,
    offense_type: selectedRequest.offense_type,
    type_class: selectedRequest.type_class,
    subtype_class: selectedRequest.subtype_class,
    ce_decision: selectedRequest.ce_decision || '',
    ground_truth: selectedRequest.ground_truth || '',
    charge_date: selectedRequest.charge_date,
    arrest_date: selectedRequest.arrest_date,
    disposition_date: selectedRequest.disposition_date,
    offense_age: selectedRequest.offense_age,
    age_at_offense: selectedRequest.age_at_offense,
    age_category: selectedRequest.age_category,
    offense_status: selectedRequest.offense_status,
    crime_category: selectedRequest.crime_category,
    adjudication_category: selectedRequest.adjudication_category,
    court_type: selectedRequest.court_type,
    additional_info: selectedRequest.additional_info,
    sentence_info: selectedRequest.sentence_info,
    estimated_release_date: selectedRequest.estimated_release_date,
    all_notes: selectedRequest.all_notes,
    rationale: selectedRequest.rationale,
    rules_applied: selectedRequest.rules_applied,
    match_status: 'UNKNOWN', // Will be computed by the dialog
  } as ComplianceDashboardData : null

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'red'
      case 'HIGH': return 'orange'
      case 'MEDIUM': return 'blue'
      case 'LOW': return 'gray'
      default: return 'gray'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'yellow'
      case 'IN_PROGRESS': return 'blue'
      case 'COMPLETED': return 'green'
      case 'CANCELLED': return 'gray'
      default: return 'gray'
    }
  }

  if (!isExpert && !isAdmin) {
    return (
      <Container fluid p="md">
        <Center style={{ minHeight: 400 }}>
          <Text c="dimmed">You don't have permission to access this page.</Text>
        </Center>
      </Container>
    )
  }

  return (
    <Container fluid p="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <IconClipboardCheck size={32} color="#2196F3" />
            <Title order={2}>Expert Feedback</Title>
          </Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            variant="light"
          >
            Refresh
          </Button>
        </Group>

        {/* Statistics Cards */}
        {stats && (
          <Group grow>
            <Card withBorder>
              <Text size="sm" c="dimmed">Pending</Text>
              <Text size="xl" fw={700}>{stats.pending}</Text>
            </Card>
            <Card withBorder>
              <Text size="sm" c="dimmed">In Progress</Text>
              <Text size="xl" fw={700}>{stats.in_progress}</Text>
            </Card>
            <Card withBorder>
              <Text size="sm" c="dimmed">Completed</Text>
              <Text size="xl" fw={700}>{stats.completed}</Text>
            </Card>
            <Card withBorder>
              <Text size="sm" c="dimmed">Total</Text>
              <Text size="xl" fw={700}>{stats.total}</Text>
            </Card>
          </Group>
        )}

        {/* Filters */}
        <Paper p="md" withBorder>
          <Group gap="md">
            <TextInput
              placeholder="Search by case number, offense ID, charge..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1 }}
            />
          </Group>
        </Paper>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(val) => { setActiveTab(val || 'todo'); setPage(1); }}>
          <Tabs.List>
            <Tabs.Tab value="todo">
              Feedbacks to Do
              {stats && stats.pending > 0 && (
                <Badge size="sm" ml="xs" color="yellow">{stats.pending}</Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="assigned">All Assigned to Me</Tabs.Tab>
            <Tabs.Tab value="completed">Feedback Submitted</Tabs.Tab>
          </Tabs.List>

          {/* Table */}
          <Tabs.Panel value={activeTab} pt="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder>
            {isLoading ? (
              <Center style={{ minHeight: 300 }}>
                <Loader size="lg" />
              </Center>
            ) : filteredRequests.length === 0 ? (
              <Center style={{ minHeight: 300 }}>
                <Text c="dimmed">
                  {activeTab === 'todo' ? 'No pending requests' : 'No requests found'}
                </Text>
              </Center>
            ) : (
              <>
                <ScrollArea h={600}>
                  <Table striped highlightOnHover style={{ cursor: 'pointer' }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Case Number</Table.Th>
                        <Table.Th>Offense ID</Table.Th>
                        <Table.Th>Search ID</Table.Th>
                        <Table.Th>Charge</Table.Th>
                        <Table.Th>Charge Desc</Table.Th>
                        <Table.Th>Disposition</Table.Th>
                        <Table.Th>Offense Type</Table.Th>
                        <Table.Th>UBS Decision</Table.Th>
                        <Table.Th>Charge Date</Table.Th>
                        <Table.Th>Arrest Date</Table.Th>
                        <Table.Th>Disposition Date</Table.Th>
                        <Table.Th>Priority</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Requested Date</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {paginatedRequests.map((request: FeedbackRequest) => (
                        <Table.Tr
                          key={request.feedback_request_id}
                          onClick={() => handleRowClick(request)}
                        >
                          <Table.Td>
                            <Text size="sm" fw={500}>{request.case_number || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{request.offense_id || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{request.search_id || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={1}>{request.charge || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={1}>{request.charge_description || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={1}>{request.disposition || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{request.offense_type || 'N/A'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge size="sm" color={request.ground_truth === 'REPORTABLE' ? 'blue' : 'gray'}>
                              {request.ground_truth || 'N/A'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {formatDate(request.charge_date)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {formatDate(request.arrest_date)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {formatDate(request.disposition_date)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge size="sm" color={getPriorityColor(request.priority)}>
                              {request.priority}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge size="sm" color={getStatusColor(request.feedback_status)}>
                              {request.feedback_status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {formatDate(request.requested_at)}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Group justify="center" mt="md">
                    <Pagination
                      value={page}
                      onChange={setPage}
                      total={totalPages}
                    />
                  </Group>
                )}
              </>
            )}
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Feedback Dialog */}
      {selectedOffense && (
        <OffenseDetailDialog
          opened={dialogOpened}
          onClose={handleDialogClose}
          offense={selectedOffense}
          allOffensesInSearch={allOffensesInSearch}
          feedbackRequest={selectedRequest}
          onFeedbackSubmitted={handleFeedbackSubmitted}
          isExpert={isExpert}
        />
      )}
    </Container>
  )
}
